import { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Stethoscope, Calculator, FlaskConical, BookOpen, GraduationCap,
  Loader2, Check, X, ChevronRight, Sparkles, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * DefenseInlineSuggestionsDialog — Sprint 7.
 *
 * Sits ON TOP of the existing DefenseSimulationDialog and converts a
 * completed 5-reviewer defense report into a flat list of Grammarly-Pro-style
 * inline suggestions the author can Accept / Reject / Skip one-by-one.
 *
 * Lifecycle:
 *   1. Caller opens dialog with `defenseId` of a `status='ready'` defense report.
 *   2. On open, we POST /api/thesis/defense/reports/{id}/inline-suggestions
 *      → returns 8-18 cached suggestions. Subsequent opens are instant.
 *   3. For each suggestion, user clicks Accept → POST /defense/apply-inline-
 *      suggestion (server splices new text into chapter.markdown), or Reject.
 *   4. `onChapterPatched(newMarkdown)` lets the parent refresh UI in place.
 */
const REVIEWER_META = {
  clinician:    { label: 'Clinician',    icon: Stethoscope,    accent: 'text-emerald-700 dark:text-emerald-300' },
  statistician: { label: 'Statistician', icon: Calculator,     accent: 'text-blue-700 dark:text-blue-300' },
  methodology:  { label: 'Methodology',  icon: FlaskConical,   accent: 'text-purple-700 dark:text-purple-300' },
  journal:      { label: 'Journal',      icon: BookOpen,       accent: 'text-amber-700 dark:text-amber-300' },
  committee:    { label: 'Committee',    icon: GraduationCap,  accent: 'text-rose-700 dark:text-rose-300' },
};

const TYPE_LABEL = {
  add: 'ADD',
  rewrite: 'REWRITE',
  clarify: 'CLARIFY',
  qualify: 'QUALIFY',
  cite: 'ADD CITATION',
};

const PRIORITY_STYLE = {
  high:   'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  low:    'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

export default function DefenseInlineSuggestionsDialog({
  open, onOpenChange,
  userId, defenseId, chapterId, language = 'en',
  onChapterPatched,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(null);
  const [decisions, setDecisions] = useState({});  // id → 'accepted' | 'rejected'

  const load = useCallback(async () => {
    if (!defenseId) return;
    setLoading(true);
    try {
      const data = await thesisApi.buildDefenseInlineSuggestions(userId, defenseId, language);
      setSuggestions(data.suggestions || []);
      if ((data.suggestions || []).length === 0) {
        toast.info('No specific inline edits surfaced — the reviewers raised high-level concerns rather than line-edit issues.');
      }
    } catch (e) {
      toast.error('Could not build suggestions: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [userId, defenseId, language]);

  useEffect(() => {
    if (open) {
      setDecisions({});
      load();
    }
  }, [open, load]);

  const accept = async (s) => {
    setApplying(s.id);
    try {
      const res = await thesisApi.applyInlineSuggestion(userId, {
        chapter_id: chapterId,
        suggestion_id: s.id,
        target_quote: s.target_quote || '',
        suggested_text: s.suggested_text,
        type: s.type,
      });
      if (!res.applied) {
        toast.warning(res.reason || 'Could not apply — target snippet no longer present.');
        setDecisions((d) => ({ ...d, [s.id]: 'skipped' }));
        return;
      }
      onChapterPatched?.(res.markdown);
      setDecisions((d) => ({ ...d, [s.id]: 'accepted' }));
      toast.success(`${REVIEWER_META[s.reviewer]?.label} suggestion applied`);
    } catch (e) {
      toast.error('Apply failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setApplying(null);
    }
  };

  const reject = (s) => setDecisions((d) => ({ ...d, [s.id]: 'rejected' }));

  const remaining = suggestions.filter((s) => !decisions[s.id]);
  const accepted = suggestions.filter((s) => decisions[s.id] === 'accepted').length;
  const rejected = suggestions.filter((s) => decisions[s.id] === 'rejected').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="defense-inline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-500" />
            Apply reviewer suggestions inline
          </DialogTitle>
          <DialogDescription>
            Grammarly-style accept/reject layer on top of your 5-reviewer defense panel.
            Each suggestion is grounded in a specific reviewer's critique and is editable.
          </DialogDescription>
        </DialogHeader>

        {loading && suggestions.length === 0 && (
          <div className="py-12 text-center">
            <Loader2 className="h-9 w-9 animate-spin mx-auto text-violet-500" />
            <p className="text-sm text-slate-600 mt-3">
              Translating reviewer critiques into actionable edits…
            </p>
            <p className="text-[11px] text-slate-400 mt-1">First build takes ~10–20 s. Cached on subsequent opens.</p>
          </div>
        )}

        {!loading && suggestions.length > 0 && (
          <>
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-3 mb-3">
              <div className="text-[12px] text-slate-600 dark:text-slate-400">
                <span className="font-semibold text-slate-800 dark:text-slate-100">{suggestions.length}</span> total
                · <span className="text-emerald-700 dark:text-emerald-300">{accepted} accepted</span>
                · <span className="text-slate-500">{rejected} rejected</span>
                · <span className="text-violet-700 dark:text-violet-300">{remaining.length} pending</span>
              </div>
              <Button size="sm" variant="ghost" onClick={load} disabled={loading}
                data-testid="defense-inline-rebuild">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Rebuild
              </Button>
            </div>

            <ol className="space-y-3" data-testid="defense-inline-list">
              {suggestions.map((s, idx) => {
                const meta = REVIEWER_META[s.reviewer] || REVIEWER_META.committee;
                const Icon = meta.icon;
                const decision = decisions[s.id];
                return (
                  <li key={s.id}
                    className={`rounded-md border ${
                      decision === 'accepted' ? 'border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/20'
                      : decision === 'rejected' ? 'border-slate-200 bg-slate-50/60 opacity-60'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
                    } p-3`}
                    data-testid={`defense-inline-item-${idx}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold ${meta.accent}`}>
                        <Icon className="h-3 w-3" /> {meta.label}
                      </span>
                      <span className="text-[9.5px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                        {TYPE_LABEL[s.type] || s.type.toUpperCase()}
                      </span>
                      <span className={`text-[9.5px] font-semibold px-1.5 py-0.5 rounded uppercase ${PRIORITY_STYLE[s.priority] || PRIORITY_STYLE.medium}`}>
                        {s.priority}
                      </span>
                    </div>
                    {s.target_quote && s.type !== 'add' && (
                      <div className="mb-2">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Original</p>
                        <p className="text-[12.5px] text-slate-600 dark:text-slate-300 italic bg-red-50/60 dark:bg-red-950/20 border-l-2 border-red-300 dark:border-red-700 pl-2 py-1 line-through decoration-red-300 decoration-1">
                          "{s.target_quote}"
                        </p>
                      </div>
                    )}
                    <div className="mb-2">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-0.5">
                        {s.type === 'add' ? 'Insert' : 'Replacement'}
                      </p>
                      <p className="text-[13px] text-slate-800 dark:text-slate-100 bg-emerald-50/60 dark:bg-emerald-950/20 border-l-2 border-emerald-300 dark:border-emerald-700 pl-2 py-1">
                        {s.suggested_text}
                      </p>
                    </div>
                    {s.reason && (
                      <p className="text-[11.5px] text-slate-500 dark:text-slate-400 italic mb-2">
                        <ChevronRight className="inline h-3 w-3 -mt-0.5" /> {s.reason}
                      </p>
                    )}
                    <div className="flex gap-1.5 pt-1">
                      <Button size="sm" onClick={() => accept(s)}
                        disabled={applying === s.id || decision === 'accepted'}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                        data-testid={`defense-inline-accept-${idx}`}>
                        {applying === s.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                        {decision === 'accepted' ? 'Accepted' : 'Accept'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(s)}
                        disabled={decision === 'rejected'}
                        className="h-7 text-xs"
                        data-testid={`defense-inline-reject-${idx}`}>
                        <X className="h-3 w-3 mr-1" />
                        {decision === 'rejected' ? 'Rejected' : 'Reject'}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ol>
          </>
        )}

        {!loading && suggestions.length === 0 && (
          <div className="py-12 text-center text-slate-500 text-sm">
            No actionable inline suggestions surfaced. Try re-running the defense panel
            with a more detailed chapter, or use the full Defense Simulation dialog for
            high-level critique.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800 mt-3">
          <Button onClick={() => onOpenChange(false)} data-testid="defense-inline-close">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
